/**
 * PlayerPreviewDrawerView — pure presentation for the player preview drawer
 * (SE-5494, restyled SE-6554).
 *
 * Right-anchored side drawer on desktop, bottom sheet on small screens — one
 * Drawer atom with `anchor` switched by SCREEN SIZE, never device type or
 * platform (see `.claude/rules/screen-size-not-device-type.md`).
 *
 * It keeps its scrim. The neighbouring pipeline drawers dropped theirs during
 * the restyle, but this one dims the board on purpose: a preview is a focused
 * read of ONE card, and the dim is what stops the columns behind it from
 * competing with the player being read.
 *
 * Section order is load-bearing and asserted by tests:
 *   hero identity + GPR → PINNED NOTE → stage → key info → ratings →
 *   attributes → reports & verdict → season form → tags → Activity (the
 *   SE-5504 audit trail) → footer actions.
 * AC1 requires the pinned note to sit ABOVE the stage section, so those two
 * blocks must not be reordered without revisiting the acceptance criteria.
 *
 * ## The header names the SURFACE, not the player
 *
 * The title is the word "Player"; the player's own name moved into the hero
 * block beneath it, beside the avatar. That is the restyle's shape and it is
 * also what lets the name take a full title rung without fighting the close
 * button for the header's height.
 * @remarks View component — no business logic, hooks, or state.
 * @module features/player-kanban/components/organisms/PlayerPreviewDrawer
 */

import { memo } from "react";
import { PlusIcon, XIcon } from "lucide-react-native";

import {
  TEST_ID_PLAYER_KANBAN,
  TEST_ID_PLAYER_PREVIEW_DRAWER,
} from "e2e/testId";

import {
  Avatar,
  AvatarBadge,
  AvatarFallbackText,
} from "@/components/atoms/Avatar";
import { TonePill } from "@/components/atoms/Badge";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import {
  Drawer,
  DrawerBackdrop,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "@/components/atoms/Drawer";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import {
  PIPELINE_DRAWER_CONTENT_CLASS,
  PIPELINE_DRAWER_SHEET_CLASS,
} from "../../../constants";

import PipelineActivityTrail from "../../molecules/PipelineActivityTrail";
import RemoveFromPipelineDialog from "../../molecules/RemoveFromPipelineDialog";
import PlayerPreviewInfoRows from "../../molecules/PlayerPreviewInfoRows";
import PlayerPreviewSection from "../../molecules/PlayerPreviewSection";
import PlayerPreviewStagePicker, {
  type PlayerPreviewStageOption,
} from "../../molecules/PlayerPreviewStagePicker";

import type { PipelineActivityRow } from "../../../utils/pipelineActivityRows";
import type {
  PlayerAvailability,
  PlayerPreviewInfoItem,
  PlayerPreviewReportItem,
} from "../../../utils/playerPreviewModel";

/** One tag chip on the card. */
export interface PlayerPreviewTag {
  /** Tag id. */
  readonly id: string;
  /** Tag display name. */
  readonly name: string;
}

/** The pinned latest note (AC1). */
export interface PlayerPreviewPinnedNote {
  /** Note id. */
  readonly id: string;
  /** Note body (falls back to the title when the body is blank). */
  readonly body: string;
  /** Formatted creation date. */
  readonly createdAt: string;
  /** Relative creation stamp, e.g. `"2 days ago"`. */
  readonly relativeCreatedAt: string;
  /** Whether the note is recent enough to badge as NEW. */
  readonly isNew: boolean;
  /** Compact age for the NEW badge, e.g. `"2d"`. */
  readonly ageLabel?: string;
  /** Author display name. */
  readonly authorName: string;
}

/** Props for {@link PlayerPreviewDrawerView}. */
export interface PlayerPreviewDrawerViewProps {
  /** Whether the drawer is open. */
  readonly isOpen: boolean;
  /** Close handler. */
  readonly onClose: () => void;
  /** Whether the small-screen (bottom-sheet) layout is active. */
  readonly isSmallScreen: boolean;
  /** Whether the player query is still loading. */
  readonly isLoading: boolean;
  /** Error message to show instead of content, when the query failed. */
  readonly errorMessage?: string;

  /** Player display name. */
  readonly playerName: string;
  /** Player's club. */
  readonly clubName: string;
  /** Position abbreviation for the hero chip; empty when unknown. */
  readonly positionLabel: string;
  /** The hero's joined meta line — club, age, nationality. */
  readonly heroMeta: string;
  /**
   * Availability for the avatar's status dot, or `undefined` when the provider
   * reported nothing — in which case NO dot renders.
   */
  readonly availability?: PlayerAvailability;
  /** Formatted GPR, or `undefined` when unknown. */
  readonly gpr?: string;

  /**
   * The single most recent note (AC1), or `undefined` when the player has none.
   * When absent, NEITHER the note block NOR the "View all notes" link renders
   * (AC2) — the whole block is omitted, not emptied.
   */
  readonly pinnedNote?: PlayerPreviewPinnedNote;
  /** Total notes on the player, for the "View all notes" counter. */
  readonly noteCount?: number;
  /** Opens the player's full notes list. */
  readonly onViewAllNotes: () => void;

  /** Stages the card may move into. */
  readonly stages: readonly PlayerPreviewStageOption[];
  /** The card's current stage id. */
  readonly currentStageId?: string;
  /** Whether the viewer may move this card. */
  readonly canEdit: boolean;
  /** Whether a stage move is in flight. */
  readonly isMoving: boolean;
  /**
   * Pre-bound press handler per stage id, built by the container and wired to
   * the board's shared move service.
   */
  readonly stageHandlers: Readonly<Record<string, () => void>>;

  /** Key info rows (eleven, always). */
  readonly keyInfoRows: readonly PlayerPreviewInfoItem[];
  /** Ratings & style-fit rows. */
  readonly ratingsRows: readonly PlayerPreviewInfoItem[];
  /** Per-skill attribute rows (carried over from the panel this replaced). */
  readonly attributesRows: readonly PlayerPreviewInfoItem[];
  /** Season-form rows. */
  readonly seasonFormRows: readonly PlayerPreviewInfoItem[];

  /** Scout reports, newest first. */
  readonly reports: readonly PlayerPreviewReportItem[];
  /** Server-side total report count, for the "Reports" counter. */
  readonly reportCount?: number;
  /** The latest report's overall score, when there is one. */
  readonly verdict?: string;

  /** Tags on the card. */
  readonly tags: readonly PlayerPreviewTag[];
  /**
   * SE-6859 — opens the board's tag selector for this card. Absent for
   * viewers without edit access, which drops the "+" affordance entirely
   * (the same gate the Table's Tags column applies).
   */
  readonly onAddTag?: () => void;

  /**
   * SE-5504 (AC1): this card's audit trail, newest-first, EXACTLY as the
   * server returned it. The View renders every row it is given — it must not
   * filter, hide or re-order, per the ticket's Out of Scope.
   */
  readonly activityRows: readonly PipelineActivityRow[];
  /** Whether the trail's first page is loading. */
  readonly activityLoading: boolean;
  /** Whether the trail read failed. */
  readonly activityIsError: boolean;
  /** Whether the trail has more pages. */
  readonly activityHasMore: boolean;
  /** Whether a trail "Load more" page is in flight. */
  readonly activityIsLoadingMore: boolean;
  /** Fetches the trail's next page. */
  readonly onLoadMoreActivity: () => void;

  /** Opens the full player profile. */
  readonly onOpenProfile: () => void;
  /**
   * SE-5500 AC1 — whether the viewer has EDIT access. Without it the remove
   * button is not rendered at all; a disabled destructive button that can
   * never be enabled is just noise.
   */
  readonly canRemove: boolean;
  /** Remove action label — pipeline boards say "pipeline", others "board". */
  readonly removeLabel: string;
  /** Opens the removal confirmation (never removes directly). */
  readonly onRemove: () => void;
  /** Whether the board is a PIPELINE board (drives the dialog's wording). */
  readonly isPipelineBoard: boolean;
  /** Whether the removal confirmation is open. */
  readonly isRemoveDialogOpen: boolean;
  /** Dismisses the removal confirmation. */
  readonly onCancelRemove: () => void;
  /** Performs the removal with the confirmed reason; rejects on failure. */
  readonly onConfirmRemove: (rationale: string) => Promise<void>;
  /** Invoked after a successful removal. */
  readonly onRemoved: () => void;
}

/** Status-dot fill per availability. */
const AVAILABILITY_DOT_CLASS: Record<PlayerAvailability, string> = {
  available: "bg-status-success",
  injured: "bg-status-error",
};

/** Spoken description of the status dot, so it is not colour-only. */
const AVAILABILITY_LABEL: Record<PlayerAvailability, string> = {
  available: "Available",
  injured: "Injured",
};

/**
 * Render the hero: avatar with its status dot, identity, and the GPR pill.
 * @param props - The full view props.
 * @returns The hero block.
 */
const renderHero = (props: PlayerPreviewDrawerViewProps) => (
  <Stack direction="horizontal" className="items-center gap-3 pb-4">
    {/* `lg` is exactly the design's 64px disc, and its AvatarBadge is exactly
        the 16px dot — both come off the atom's own size ladder rather than a
        hand-set height. The near-black fill with white initials is the same
        pairing every player card in the app already draws (SE-5538), which is
        what keeps the drawer and the card it opened from showing the same
        player two different ways. */}
    <Avatar size="lg" className="shrink-0 bg-surface-inverse">
      <AvatarFallbackText className="text-content-inverse">
        {props.playerName}
      </AvatarFallbackText>
      {/* Rendered ONLY when the provider actually reported availability —
          `undefined` means unknown, and a green disc drawn off unknown would
          assert a clean bill of health nobody gave. */}
      {props.availability === undefined ? null : (
        <AvatarBadge
          className={AVAILABILITY_DOT_CLASS[props.availability]}
          accessibilityLabel={AVAILABILITY_LABEL[props.availability]}
          testID={TEST_ID_PLAYER_PREVIEW_DRAWER.AVAILABILITY_DOT}
        />
      )}
    </Avatar>

    <Stack className="min-w-0 flex-1 gap-1">
      <Text
        variant="title"
        className="text-content-primary"
        testID={TEST_ID_PLAYER_PREVIEW_DRAWER.PLAYER_NAME}
        isTruncated
      >
        {props.playerName}
      </Text>
      <Stack direction="horizontal" className="items-center gap-1">
        {props.positionLabel === "" ? null : (
          <Box className="shrink-0 rounded-sm bg-surface-raised px-2 py-px">
            <Text variant="micro-strong" className="text-content-primary">
              {props.positionLabel}
            </Text>
          </Box>
        )}
        <Text
          variant="caption"
          className="min-w-0 flex-1 text-content-secondary"
          testID={TEST_ID_PLAYER_PREVIEW_DRAWER.PLAYER_CLUB}
          isTruncated
        >
          {props.heroMeta}
        </Text>
      </Stack>
    </Stack>

    {props.gpr ? (
      <TonePill
        tone="success"
        label={props.gpr}
        textVariant="body-strong"
        className="h-8 w-10 shrink-0"
        testID={TEST_ID_PLAYER_PREVIEW_DRAWER.GPR_BADGE}
      />
    ) : null}
  </Stack>
);

/**
 * Render the pinned-note block, or nothing at all (AC2).
 * @param props - The full view props.
 * @returns The note block, or `null`.
 */
const renderPinnedNote = (props: PlayerPreviewDrawerViewProps) => {
  // AC2: no notes → render NOTHING. Not an empty state, not a bare
  // "View all notes" link — the entire block is absent.
  if (!props.pinnedNote) return null;

  const note = props.pinnedNote;

  return (
    <PlayerPreviewSection
      title="Latest note"
      isLeading
      testID={TEST_ID_PLAYER_PREVIEW_DRAWER.PINNED_NOTE}
      headerAccessory={
        // Time-boxed: the note PINS regardless of age (AC1), and only this
        // badge is bounded by the recency window.
        note.isNew && note.ageLabel !== undefined ? (
          <TonePill
            tone="info-soft"
            label={`NEW · ${note.ageLabel}`}
            textVariant="micro"
            className="shrink-0 px-2 py-micro"
            testID={TEST_ID_PLAYER_PREVIEW_DRAWER.PINNED_NOTE_NEW_BADGE}
          />
        ) : undefined
      }
    >
      <Stack space="2">
        <Box className="rounded-md bg-surface-raised px-3 py-2">
          <Text
            variant="caption"
            className="text-content-primary"
            testID={TEST_ID_PLAYER_PREVIEW_DRAWER.PINNED_NOTE_BODY}
          >
            {note.body}
          </Text>
        </Box>
        <Stack
          direction="horizontal"
          space="2"
          className="items-center justify-between"
        >
          <Text variant="micro" className="min-w-0 flex-1 text-content-muted">
            {`${note.authorName} · ${note.relativeCreatedAt}`}
          </Text>
          <Pressable
            onPress={props.onViewAllNotes}
            accessibilityLabel="View all notes"
            accessibilityRole="button"
            testID={TEST_ID_PLAYER_PREVIEW_DRAWER.VIEW_ALL_NOTES}
            className="shrink-0"
          >
            <Text variant="micro-strong" className="text-content-primary">
              {props.noteCount === undefined
                ? "View all notes"
                : `View all notes (${props.noteCount})`}
            </Text>
          </Pressable>
        </Stack>
      </Stack>
    </PlayerPreviewSection>
  );
};

/**
 * Render the reports & verdict section body.
 * @param props - The full view props.
 * @returns The section body.
 */
const renderReports = (props: PlayerPreviewDrawerViewProps) => {
  if (!props.reports.length) {
    return (
      <Text
        variant="caption"
        className="text-content-muted"
        testID={TEST_ID_PLAYER_PREVIEW_DRAWER.REPORTS_EMPTY}
      >
        No scout reports yet.
      </Text>
    );
  }

  return (
    <Stack space="2">
      <Stack
        direction="horizontal"
        space="3"
        className="items-center justify-between"
      >
        <Text variant="caption" className="text-content-secondary">
          Reports
        </Text>
        <Text variant="caption" className="font-medium text-content-primary">
          {String(props.reportCount ?? props.reports.length)}
        </Text>
      </Stack>

      {props.verdict ? (
        <Stack
          direction="horizontal"
          space="3"
          className="items-center justify-between"
        >
          <Text variant="caption" className="text-content-secondary">
            {/*
             * "Latest verdict" and not "Verdict": `PlayerScoutReport` has no
             * verdict field, so this chip is the most recent report's overall
             * SCORE. The design's categorical chip ("Yes · Starting XI") has
             * nothing behind it in this schema, and labelling a score as a
             * scout's verdict is the misreading the wording avoids.
             */}
            Latest verdict
          </Text>
          <TonePill
            tone="success-soft"
            label={props.verdict}
            textVariant="micro"
            className="shrink-0 px-2 py-micro"
            testID={TEST_ID_PLAYER_PREVIEW_DRAWER.VERDICT}
          />
        </Stack>
      ) : null}

      <Stack>
        {props.reports.map(report => (
          <Stack
            key={report.key}
            direction="horizontal"
            space="3"
            className="items-center justify-between border-t border-outline-subtle py-2"
            testID={TEST_ID_PLAYER_PREVIEW_DRAWER.reportRowFor(report.key)}
          >
            <Text
              variant="caption"
              className="min-w-0 flex-1 text-content-primary"
              isTruncated
            >
              {report.scoutName}
            </Text>
            <Text variant="micro" className="shrink-0 text-content-muted">
              {report.overallScore
                ? `${report.overallScore} · ${report.reportDate}`
                : report.reportDate}
            </Text>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

/**
 * The tags section's "+" add affordance — SE-6859.
 * @remarks
 * The same bare-glyph idiom the Table's Tags column uses: a plus and an `Add`
 * word, muted, rather than a bordered pill that would compete with the real
 * chips beside it. Pressing it opens the board's existing tag selector — the
 * identical picker/creation/persistence flow the Table's `Add` reaches — so
 * the drawer adds an entry point, not a second editing surface.
 * @param onAddTag - Opens the tag selector for this card.
 * @returns The affordance element.
 */
const renderAddTag = (onAddTag: () => void) => (
  <Pressable
    accessibilityRole="button"
    accessibilityLabel="Add a tag"
    onPress={onAddTag}
    testID={TEST_ID_PLAYER_PREVIEW_DRAWER.TAGS_ADD}
  >
    <Stack direction="horizontal" className="items-center gap-1 py-1">
      <Icon as={PlusIcon} size="2xs" className="text-content-muted" />
      <Text variant="micro" className="text-content-muted">
        Add
      </Text>
    </Stack>
  </Pressable>
);

/**
 * Render the tags section body.
 * @param tags - Tags on the card.
 * @param onAddTag - Opens the tag selector; absent for viewers who may not
 * edit, which drops the "+" affordance (SE-6859).
 * @returns The section body.
 */
const renderTags = (
  tags: readonly PlayerPreviewTag[],
  onAddTag?: () => void
) => {
  if (!tags.length) {
    return (
      <Stack direction="horizontal" className="items-center gap-2">
        <Text
          variant="caption"
          className="text-content-muted"
          testID={TEST_ID_PLAYER_PREVIEW_DRAWER.TAGS_EMPTY}
        >
          No tags.
        </Text>
        {onAddTag ? renderAddTag(onAddTag) : null}
      </Stack>
    );
  }

  return (
    <Stack direction="horizontal" className="flex-wrap items-center gap-2">
      {tags.map(tag => (
        <TonePill
          key={tag.id}
          tone="neutral-raised"
          label={tag.name}
          textVariant="micro"
          className="px-3 py-1"
          testID={TEST_ID_PLAYER_PREVIEW_DRAWER.tagFor(tag.id)}
        />
      ))}
      {onAddTag ? renderAddTag(onAddTag) : null}
    </Stack>
  );
};

/**
 * Render the drawer body: spinner, error, or the full section stack.
 * @param props - The full view props.
 * @returns The body content.
 */
const renderBody = (props: PlayerPreviewDrawerViewProps) => {
  if (props.isLoading) {
    return (
      <Box
        className="items-center justify-center py-10"
        testID={TEST_ID_PLAYER_PREVIEW_DRAWER.LOADING}
      >
        <Spinner />
      </Box>
    );
  }

  if (props.errorMessage) {
    return (
      <Box
        className="items-center justify-center py-10"
        testID={TEST_ID_PLAYER_PREVIEW_DRAWER.ERROR_STATE}
      >
        <Text variant="body" className="text-status-error">
          {props.errorMessage}
        </Text>
      </Box>
    );
  }

  return (
    <Stack>
      {renderHero(props)}

      {/* AC1: the pinned note sits ABOVE the stage section. */}
      {renderPinnedNote(props)}

      <PlayerPreviewSection
        title="Pipeline stage"
        // Leads only when the note above it is absent, so the drawer never
        // opens on a rule with nothing above it.
        isLeading={!props.pinnedNote}
      >
        <Stack space="2">
          <PlayerPreviewStagePicker
            stages={props.stages}
            currentStageId={props.currentStageId}
            canEdit={props.canEdit}
            isMoving={props.isMoving}
            stageHandlers={props.stageHandlers}
          />
          <Text variant="caption" className="text-content-muted">
            Change the stage here or drag the card on the board. Sensitive
            stages ask for a reason.
          </Text>
        </Stack>
      </PlayerPreviewSection>

      <PlayerPreviewSection
        title="Key info"
        testID={TEST_ID_PLAYER_PREVIEW_DRAWER.KEY_INFO_SECTION}
      >
        <PlayerPreviewInfoRows rows={props.keyInfoRows} layout="grid" />
      </PlayerPreviewSection>

      <PlayerPreviewSection
        title="Ratings & fit"
        testID={TEST_ID_PLAYER_PREVIEW_DRAWER.RATINGS_SECTION}
      >
        <PlayerPreviewInfoRows rows={props.ratingsRows} layout="meter" />
      </PlayerPreviewSection>

      <PlayerPreviewSection
        title="Attributes"
        testID={TEST_ID_PLAYER_PREVIEW_DRAWER.ATTRIBUTES_SECTION}
      >
        <PlayerPreviewInfoRows rows={props.attributesRows} layout="meter" />
      </PlayerPreviewSection>

      <PlayerPreviewSection
        title="Reports & verdict"
        testID={TEST_ID_PLAYER_PREVIEW_DRAWER.REPORTS_SECTION}
      >
        {renderReports(props)}
      </PlayerPreviewSection>

      <PlayerPreviewSection
        title="Season form"
        testID={TEST_ID_PLAYER_PREVIEW_DRAWER.SEASON_FORM_SECTION}
      >
        <PlayerPreviewInfoRows rows={props.seasonFormRows} layout="grid" />
      </PlayerPreviewSection>

      <PlayerPreviewSection
        title="Tags"
        testID={TEST_ID_PLAYER_PREVIEW_DRAWER.TAGS_SECTION}
      >
        {renderTags(props.tags, props.onAddTag)}
      </PlayerPreviewSection>

      {/*
       * SE-5504 (AC1/AC2): the slot SE-5494 reserved, now filled with this
       * CARD's audit trail — chronological (newest-first), typed, with the
       * automated-vs-manual marker on each entry. Scoped server-side by
       * `kanbanCardId`; every entry received is rendered.
       */}
      <PlayerPreviewSection
        title="Activity"
        testID={TEST_ID_PLAYER_PREVIEW_DRAWER.ACTIVITY_SECTION}
      >
        <PipelineActivityTrail
          testID={TEST_ID_PLAYER_PREVIEW_DRAWER.ACTIVITY_TRAIL}
          rows={props.activityRows}
          loading={props.activityLoading}
          isError={props.activityIsError}
          hasMore={props.activityHasMore}
          isLoadingMore={props.activityIsLoadingMore}
          onLoadMore={props.onLoadMoreActivity}
          emptyLabel="No activity yet."
        />
      </PlayerPreviewSection>
    </Stack>
  );
};

/**
 * Render the player preview drawer.
 * @param props - {@link PlayerPreviewDrawerViewProps}
 * @returns The drawer.
 */
const PlayerPreviewDrawerView = (props: PlayerPreviewDrawerViewProps) => (
  <>
    <Drawer
      isOpen={props.isOpen}
      onClose={props.onClose}
      anchor={props.isSmallScreen ? "bottom" : "right"}
      size={props.isSmallScreen ? "lg" : "md"}
    >
      <DrawerBackdrop />
      <DrawerContent
        // Carries the LEGACY sidebar id as well as its own, so the specs written
        // against the panel this replaces keep asserting on a live surface.
        testID={TEST_ID_PLAYER_KANBAN.PLAYER_DETAILS_SIDEBAR}
        className={
          props.isSmallScreen
            ? PIPELINE_DRAWER_SHEET_CLASS
            : PIPELINE_DRAWER_CONTENT_CLASS
        }
      >
        <DrawerHeader className="border-b border-outline-default px-5 py-4">
          <Stack
            direction="horizontal"
            className="flex-1 items-center justify-between gap-4"
          >
            <Text variant="title-sm" className="text-content-primary">
              Player
            </Text>
            <Pressable
              onPress={props.onClose}
              accessibilityLabel="Close player preview"
              testID={TEST_ID_PLAYER_KANBAN.SIDEBAR_CLOSE_BUTTON}
              className="size-8 shrink-0 items-center justify-center rounded-md bg-surface-raised"
            >
              <Icon as={XIcon} size="sm" className="text-content-secondary" />
            </Pressable>
          </Stack>
        </DrawerHeader>

        {/* ONE scroller on this axis. `DrawerBody` IS a ScrollView (the drawer
            atom binds `Body: ScrollView`), so the explicit `<ScrollView>` that
            used to sit in here made two nested vertical scrollers — the exact
            thing `AddPlayersPanelView` already documents as forbidden. The
            inner one took `flex-basis: auto` inside the outer one's content
            container, grew to its full content height, and never scrolled;
            the outer one then clipped the tail, which is why the ACTIVITY
            section could not be reached (Raniel, 2026-08-07).

            `pb-2` rather than `pb-6`: the atom's own base style carries a
            `mb-6`, which survives the merge (`mt-4 mb-6 flex-1 p-0`) and is
            the "margin bottom" he named. It cannot be reset from here — a
            `mb-0` would be a margin class, which the design-system-no-margin
            rule forbids in feature code — so the scrolling inset is trimmed to
            8px instead, leaving 8 + 24 = 32px of clearance above the footer. */}
        <DrawerBody className="flex-1 p-0" showsVerticalScrollIndicator={false}>
          {/* ROOT sits on the body wrapper, not on the hero inside it: the
              hero renders only once the player query has resolved, and the
              specs wait on ROOT to know the drawer OPENED — which is true
              while it is still loading and stays true if the query fails. */}
          <Stack
            className="px-5 pb-2 pt-4"
            testID={TEST_ID_PLAYER_PREVIEW_DRAWER.ROOT}
          >
            {renderBody(props)}
          </Stack>
        </DrawerBody>

        {/* `grow shrink basis-auto`, NOT `flex-1` (Raniel, 2026-08-07).
            `flex-1` is `1 1 0%` — a zero basis, so the two buttons took exactly
            equal halves regardless of their labels, and the 18-character
            "Remove from pipeline" wrapped to two lines in its half while the
            12-character "Open profile" sat comfortably in the other. A content
            basis lets each button start at its label's width and share the
            leftover space proportionally, so both fit on one line and the pair
            still fills the footer. The label itself is UNCHANGED — the PRD
            names the action "Remove from pipeline", so it is given room rather
            than shortened. `labelNumberOfLines={1}` makes single-line a
            guarantee: if a longer translation ever outgrows the footer it
            ellipsizes instead of silently wrapping again. */}
        <DrawerFooter className="border-t border-outline-default px-5 py-4">
          <Stack direction="horizontal" space="2" className="w-full">
            <Button
              testID={TEST_ID_PLAYER_PREVIEW_DRAWER.OPEN_PROFILE}
              label="Open profile"
              accessibilityLabel="Open player profile"
              variant="outline"
              tone="secondary"
              size="sm"
              labelNumberOfLines={1}
              className="h-9 shrink grow basis-auto"
              onPress={props.onOpenProfile}
            />
            {props.canRemove ? (
              <Button
                testID={TEST_ID_PLAYER_PREVIEW_DRAWER.REMOVE_FROM_PIPELINE}
                label={props.removeLabel}
                accessibilityLabel={props.removeLabel}
                variant="outline"
                tone="secondary"
                size="sm"
                labelNumberOfLines={1}
                className="h-9 shrink grow basis-auto"
                onPress={props.onRemove}
              />
            ) : null}
          </Stack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
    {/*
     * SE-5500 — the confirmation is a SIBLING of the drawer rather than a
     * child of its content, so the alert dialog's own overlay stacks above the
     * drawer instead of being clipped inside it.
     */}
    {/*
     * Keyed on open/closed so the dialog's error and in-flight state reset
     * when it closes. It never unmounts on its own — only `isOpen` toggles —
     * so without this, a user who hit a failed removal, closed, then reopened
     * on a DIFFERENT player would be greeted by the previous failure's error.
     *
     * Key-reset rather than a reset effect (`react-hooks/set-state-in-effect`
     * forbids that form). The key lives at the call site rather than in a
     * wrapper component because a feature Container file may hold only one
     * component (`no-inline-component-in-container`).
     *
     * It tracks `isOpen`, not the player: a FAILED removal keeps the dialog
     * open, so the instance survives and the typed reason is still there for
     * the retry — which is the behaviour SE-5500 asks for.
     */}
    <RemoveFromPipelineDialog
      key={props.isRemoveDialogOpen ? "remove-open" : "remove-closed"}
      isOpen={props.isRemoveDialogOpen}
      playerName={props.playerName}
      isPipelineBoard={props.isPipelineBoard}
      onCancel={props.onCancelRemove}
      onConfirm={props.onConfirmRemove}
      onRemoved={props.onRemoved}
    />
  </>
);

PlayerPreviewDrawerView.displayName = "PlayerPreviewDrawerView";

export default memo(PlayerPreviewDrawerView);
